<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, provide, reactive, ref } from 'vue'

import { hashString } from '../../../utils/strings'
import { TabContainerKey } from './tab-container-context'

import type { TabRegistration } from './tab-container-context'

withDefaults(defineProps<{
  headerBorderVisible?: boolean
}>(), {
  headerBorderVisible: false
})

const tabs = reactive<TabRegistration[]>([])
const activeTab = ref(0)
const slideDirection = ref<'left' | 'right'>('right')
const indicatorStyle = ref({ width: '0px', transform: 'translateX(0px)' })
const uniqueId = ref<string | null>(null)
const userInteracted = ref(false)
const tabButtons = ref<HTMLButtonElement[]>([])

const updateIndicator = () => {
  nextTick(() => {
    const activeButton = tabButtons.value[activeTab.value]
    if (!activeButton?.parentElement) return

    const buttonRect = activeButton.getBoundingClientRect()
    const headerRect = activeButton.parentElement.getBoundingClientRect()
    indicatorStyle.value = {
      width: `${buttonRect.width}px`,
      transform: `translateX(${buttonRect.left - headerRect.left}px)`
    }
  })
}

const selectTab = (index: number, shouldFocus = true) => {
  if (index < 0 || index >= tabs.length) return

  if (shouldFocus) {
    userInteracted.value = true
  }

  slideDirection.value = index > activeTab.value ? 'right' : 'left'
  activeTab.value = index
  tabs.forEach((tab, i) => {
    tab.isActive = (i === index)
    tab.tabIndex = i
  })

  updateIndicator()

  if (shouldFocus && userInteracted.value) {
    nextTick(() => {
      const activeButton = tabButtons.value[activeTab.value]
      if (activeButton && document.body.classList.contains('bx-js-user-is-tabbing')) {
        activeButton.focus()
      }
    })
  }
}

const registerTab = (tab: TabRegistration) => {
  tabs.push(tab)
  const tabTitles = tabs.map(t => t.title).join('-')
  uniqueId.value = `tabs-${hashString(tabTitles)}`

  if (tabs.length === 1 || tab.selected) {
    selectTab(tabs.length - 1, false)
  }
}

const unregisterTab = (tab: TabRegistration) => {
  const index = tabs.indexOf(tab)
  if (index === -1) return

  tabs.splice(index, 1)
  if (index === activeTab.value && tabs.length) {
    selectTab(0, false)
  }
}

const handleKeydown = (event: KeyboardEvent) => {
  userInteracted.value = true

  let newIndex = activeTab.value

  switch (event.key) {
    case 'ArrowLeft':
    case 'ArrowUp':
      event.preventDefault()
      newIndex = activeTab.value - 1
      if (newIndex < 0) newIndex = tabs.length - 1
      break
    case 'ArrowRight':
    case 'ArrowDown':
      event.preventDefault()
      newIndex = activeTab.value + 1
      if (newIndex >= tabs.length) newIndex = 0
      break
    case 'Home':
      event.preventDefault()
      newIndex = 0
      break
    case 'End':
      event.preventDefault()
      newIndex = tabs.length - 1
      break
    default:
      return
  }

  selectTab(newIndex)
}

provide(TabContainerKey, { registerTab, unregisterTab, containerId: uniqueId })

onMounted(() => {
  updateIndicator()
  window.addEventListener('resize', updateIndicator)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateIndicator)
})
</script>

<template>
  <div class="bx-tabs__container bx-typo--tabs"
       role="tablist"
       :class="{ 'slide-left': slideDirection === 'left', 'slide-right': slideDirection === 'right' }">
    <div class="bx-tabs__header"
         :class="{ 'bx-tabs__header--border-visible': headerBorderVisible }">
      <button
        v-for="(tab, index) in tabs"
        :id="`tab-${uniqueId}-${index}`"
        ref="tabButtons"
        :key="index"
        role="tab"
        :aria-controls="`panel-${uniqueId}-${index}`"
        :aria-selected="activeTab === index"
        :class="['bx-tabs__button', { 'bx-active': activeTab === index }]"
        :tabindex="activeTab === index ? 0 : -1"
        @click="selectTab(index)"
        @keydown="handleKeydown">
        {{ tab.title }}
      </button>
      <div class="bx-active-indicator"
           :style="indicatorStyle" />
    </div>
    <div class="bx-tabs__content">
      <slot />
    </div>
  </div>
</template>
